// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { AppState } from '../../../../../../core/reducers';
// Layout
// CRUD
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
// Services and Models
import {
  PharmacyGroup,
  PharmacyGroupActionTypes,
  PharmacyGroupOnServerCreated,
  PharmacyGroupOnServerUpdated,
  selectPharmacyGroupById,
  selectPharmacyGroupError,
  selectPharmacyGroupLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';
import { GeneralValidationMessagesService } from '../../../../../../core/services/general-validation-messages.service';
import { NavigationService } from '../../../../../../core/services/navigation.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-pharmacy-group-edit',
  templateUrl: './pharmacy-group-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PharmacyGroupEditComponent implements OnInit, OnDestroy {
  // Public properties
  pharmacyGroup: PharmacyGroup;
  pharmacyGroupId$: Observable<number>;
  companyId: number;
  oldPharmacyGroup: PharmacyGroup;
  loadingSubject = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean>;
  pharmacyGroupForm: FormGroup;
  hasFormErrors = false;
  isViewMode: boolean;
  clicked: boolean;
  isViewMode$: Observable<boolean>;
  selectedTab: number;
  // sticky portlet header margin
  private headerMargin: number;
  // Subscriptions
  private subscriptions: Subscription[] = [];
  urbanizationValue;
  Address1Value;
  Address2Value;
  cityValue;
  zipValue;
  params: any;

  /**
   * Component constructor
   *
   * @param navigationService
   * @param store
   * @param activatedRoute
   * @param router
   * @param pharmacyGroupFB
   * @param layoutUtilsService
   * @param translate
   * @param cdr
   * @param globalService
   */
  constructor(
    private navigationService: NavigationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private pharmacyGroupFB: FormBuilder,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private globalService: GeneralValidationMessagesService,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    // this line due to make the navigation service return to pharmacy groups list if there is no ->
    // navigation stack saved before in special cases
    this.navigationService.setDefaultRoot('/lists/pharmacy-groups');

    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = params;
    });
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      if (!res) {
        this.router.navigateByUrl('/lists/pharmacy-groups');
      }
      this.companyId = res.companyID;
    });
    const errorSubscription = this.store
      .pipe(select(selectPharmacyGroupError), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Update,
            3000,
            true,
            false,
          );
          this.clicked = false;
        }
      });
    this.subscriptions.push(errorSubscription);
    const successSubscription = this.store
      .pipe(select(selectPharmacyGroupLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case PharmacyGroupActionTypes.PharmacyGroupCreated:
              successMsg = this.translate.instant('PHARMACY_GROUP.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case PharmacyGroupActionTypes.PharmacyGroupUpdated:
              successMsg = this.translate.instant('PHARMACY_GROUP.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
            this.goBack();
          }
        }
      });
    this.subscriptions.push(successSubscription);
    this.subscriptions.push(userSub);
    this.loading$ = this.loadingSubject.asObservable();
    this.loadingSubject.next(true);
    const routeSub = this.activatedRoute.params.subscribe((params) => {
      const id = params.id;
      if (id && id > 0) {
        this.store.pipe(select(selectPharmacyGroupById(id))).subscribe((result) => {
          if (result) {
            this.pharmacyGroup = result;
            this.loadPharmacyGroup();
            return;
          }
          const pharmacyGroupById = this.globalService
            .getEntitiesById('pharmacy_groups', id)
            .subscribe((res) => {
              if (res) {
                this.pharmacyGroup = res.data;
                this.loadPharmacyGroup();
                this.cdr.detectChanges();
                return;
              }
            });
          this.subscriptions.push(pharmacyGroupById);
        });
      } else {
        this.pharmacyGroup = new PharmacyGroup();
        this.loadPharmacyGroup();
      }
      this.cdr.detectChanges();
    });
    this.subscriptions.push(routeSub);
    // sticky portlet header
    window.onload = () => {
      const style = getComputedStyle(document.getElementById('kt_header'));
      this.headerMargin = parseInt(style.height, 0);
    };
  }

  loadPharmacyGroup() {
    if (!this.pharmacyGroup) {
      this.goBack();
      return;
    }
    this.pharmacyGroupId$ = of(this.pharmacyGroup.id);
    this.oldPharmacyGroup = Object.assign({}, this.pharmacyGroup);
    this.initPharmacyGroup();
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init pharmacyGroup
   */
  initPharmacyGroup() {
    this.createForm();
    this.loadingSubject.next(false);
    if (!this.pharmacyGroup.id) {
      this.isViewMode = false;
    } else if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      this.isViewMode = true;
    } else {
      this.isViewMode = false;
    }
    this.isViewMode$ = of(this.isViewMode);
  }

  /**
   * Create form
   */
  createForm() {
    this.pharmacyGroupForm = this.pharmacyGroupFB.group({
      name: [this.pharmacyGroup.name, Validators.required],
      email: [
        this.pharmacyGroup.email,
        Validators.pattern(
          /^\s*([A-Za-z0-9\-_.]+@[A-Za-z0-9]+\.[a-z]+;\s*)*([A-Za-z0-9\-_.]+@[A-Za-z0-9]+\.[a-z]+\s*)?$/,
        ),
      ],
      phone: [
        this.pharmacyGroup.phone && this.pharmacyGroup.phone !== ''
          ? '(' +
            this.pharmacyGroup.phone.substring(0, 3) +
            ') ' +
            this.pharmacyGroup.phone.substring(3, 6) +
            '-' +
            this.pharmacyGroup.phone.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      fax: [
        this.pharmacyGroup.fax && this.pharmacyGroup.fax !== ''
          ? '(' +
            this.pharmacyGroup.fax.substring(0, 3) +
            ') ' +
            this.pharmacyGroup.fax.substring(3, 6) +
            '-' +
            this.pharmacyGroup.fax.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/),
      ],
      preferred_communication_method: [this.pharmacyGroup.preferred_communication_method],
      urbanization: [this.pharmacyGroup.urbanization, Validators.maxLength(48)],
      city: [
        this.pharmacyGroup.city,
        Validators.compose([Validators.required, Validators.maxLength(30)]),
      ],
      state: [this.pharmacyGroup.state, Validators.required],
      zip: [
        this.pharmacyGroup.zip,
        Validators.compose([Validators.required, Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)]),
      ],
      address1: [
        this.pharmacyGroup.address_1,
        Validators.compose([Validators.required, Validators.maxLength(48)]),
      ],
      address2: [this.pharmacyGroup.address_2, Validators.maxLength(48)],
      note: [this.pharmacyGroup.note],
      is_active: [this.pharmacyGroup.is_active ? this.pharmacyGroup.is_active : false],
    });
    if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      this.pharmacyGroupForm.disable();
    }
    this.urbanizationValue = this.pharmacyGroup.urbanization;
    this.Address1Value = this.pharmacyGroup.address_1;
    this.Address2Value = this.pharmacyGroup.address_2;
    this.cityValue = this.pharmacyGroup.city;
    this.zipValue = this.pharmacyGroup.zip;
  }

  goBack() {
    this.loadingSubject.next(false);
    this.navigationService.goBack();
  }

  /**
   * Reset
   */
  reset() {
    this.pharmacyGroup = Object.assign({}, this.oldPharmacyGroup);
    this.createForm();
    this.pharmacyGroupForm.controls[`entity_type`].enable();
    this.hasFormErrors = false;
    this.pharmacyGroupForm.markAsPristine();
    this.pharmacyGroupForm.markAsUntouched();
    this.pharmacyGroupForm.updateValueAndValidity();
  }

  /**
   * Save data
   *
   * @param withBack: boolean
   */
  onSumbit() {
    this.hasFormErrors = false;
    this.clicked = true;
    const controls = this.pharmacyGroupForm.controls;
    /** check form */
    if (this.pharmacyGroupForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      this.selectedTab = 0;
      return;
    }
    // tslint:disable-next-line:prefer-const
    const editedPharmacyGroup = this.preparePharmacyGroup();
    if (editedPharmacyGroup.id > 0) {
      this.updatePharmacyGroup(editedPharmacyGroup);
      return;
    }
    this.addPharmacyGroup(editedPharmacyGroup);
  }

  /**
   * Returns object for saving
   */
  preparePharmacyGroup(): PharmacyGroup {
    const controls = this.pharmacyGroupForm.controls;
    const _pharmacyGroup = new PharmacyGroup();
    _pharmacyGroup.id = this.pharmacyGroup.id;
    _pharmacyGroup.name = controls.name.value;
    _pharmacyGroup.email = controls.email.value;
    _pharmacyGroup.phone =
      controls.phone.value !== null
        ? controls.phone.value.replace(/\D/g, '')
        : controls.phone.value;
    _pharmacyGroup.fax =
      controls.fax.value !== null ? controls.fax.value.replace(/\D/g, '') : controls.fax.value;
    _pharmacyGroup.preferred_communication_method = controls.preferred_communication_method.value;
    _pharmacyGroup.city = controls.city.value;
    _pharmacyGroup.state = controls.state.value ? controls.state.value.toUpperCase() : '';
    _pharmacyGroup.zip = controls.zip.value;
    _pharmacyGroup.address_1 = controls.address1.value;
    _pharmacyGroup.address_2 = controls.address2.value;
    _pharmacyGroup.note = controls.note.value;
    _pharmacyGroup.is_active = controls.is_active.value;
    _pharmacyGroup.urbanization = controls.urbanization.value;
    for (const [key, value] of Object.entries(_pharmacyGroup)) {
      if (typeof value === 'string') {
        _pharmacyGroup[key] = value.trim();
      }
    }
    return _pharmacyGroup;
  }

  /**
   * Add pharmacyGroup
   *
   * @param _pharmacyGroup: PharmacyGroup
   */
  addPharmacyGroup(_pharmacyGroup: PharmacyGroup) {
    this.loadingSubject.next(true);
    this.store.dispatch(new PharmacyGroupOnServerCreated({ pharmacyGroup: _pharmacyGroup }));
  }

  /**
   * Update pharmacyGroup
   *
   * @param _pharmacyGroup: PharmacyGroup
   * @param withBack: boolean
   */
  updatePharmacyGroup(_pharmacyGroup: PharmacyGroup) {
    this.loadingSubject.next(true);

    const updatePharmacyGroup: Update<PharmacyGroup> = {
      id: _pharmacyGroup.id,
      changes: _pharmacyGroup,
    };

    this.store.dispatch(
      new PharmacyGroupOnServerUpdated({
        partialPharmacyGroup: updatePharmacyGroup,
        pharmacyGroup: _pharmacyGroup,
      }),
    );
  }

  /**
   * Checking control validation
   *
   * @param controlName: string => Equals to formControlName
   * @param validationType: string => Equals to valitors name
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.pharmacyGroupForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }

  /**
   * Rerurns status string
   */
  getStatusString(): string {
    switch (this.pharmacyGroupForm.get('is_active').value) {
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
    }
    return '';
  }

  editPharmacyGroup(id) {
    this.navigationService.navigateTo('lists/pharmacy-groups/edit/' + id);
  }

  getTitle() {
    let result = 'New Pharmacy Group';
    if (!this.pharmacyGroup || !this.pharmacyGroup.id) {
      return result;
    }
    result = `Pharmacy Group - ${this.pharmacyGroup.name}`;
    return result;
  }

  putDash(e) {
    let x = e;
    if (x.length === 6 && x.charAt(5) !== '-') {
      x = x.slice(0, 5) + '-' + x.charAt(x.length - 1);
    }
    return x;
  }
}

